/**
 * Chat attachments for a David message.
 *
 * Every file is uploaded to private storage before the message is sent
 * (see uploadChatAttachment / uploadFile in features/files/api.ts), and only
 * compact references ride the chat request in forwardedProps. Images and PDFs
 * resolve back to bytes for the turn; CSV and Excel files stay as file IDs and
 * are read by tools.
 *
 * These caps mirror hoku/attachments.py, which is the authority: the server
 * re-checks the media type from the file's magic bytes and drops anything it
 * can't send to the model.
 */

import type { FileUIPart } from 'ai'

export const XLSX_MEDIA_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

const ACCEPTED_MEDIA_TYPES: string[] = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/csv',
  XLSX_MEDIA_TYPE,
]

export const ACCEPT_ATTR = `${ACCEPTED_MEDIA_TYPES.join(',')},.csv,.xlsx`
export const MAX_ATTACHMENTS = 5
const MAX_IMAGE_BYTES = 10 * 1024 * 1024
const MAX_PDF_BYTES = 20 * 1024 * 1024
const MAX_CSV_BYTES = 5 * 1024 * 1024
// The provider caps the whole request, not each file, so five individually
// legal files can still be refused. Mirrors MAX_TOTAL_ATTACHMENT_BYTES.
const MAX_TOTAL_BYTES = 20 * 1024 * 1024

/** A file picked in the composer but not yet sent. */
export interface PendingAttachment {
  id: string
  file: File
  filename: string
  mediaType: string
  /** Object URL for the local thumbnail; revoked when the chip is dropped. */
  url: string
  /** Storage ID once the send path uploads the file. */
  fileId?: string
}

export interface CsvAttachmentRef {
  fileId: string
  filename: string
  sizeBytes: number
}

// The server re-derives media type and size from the stored bytes, so the
// reference carries only what it cannot derive.
export interface ChatAttachmentRef {
  fileId: string
  filename: string
}

const isCsvFile = (file: File) => file.name.toLowerCase().endsWith('.csv')
const isXlsxFile = (file: File) => file.name.toLowerCase().endsWith('.xlsx')
export const isStorageBackedFile = (file: File) =>
  isCsvFile(file) || isXlsxFile(file)

export const isStorageBackedAttachment = (attachment: { mediaType: string }) =>
  attachment.mediaType === 'text/csv' ||
  attachment.mediaType === XLSX_MEDIA_TYPE

function normalizedMediaType(file: File): string {
  if (isCsvFile(file)) return 'text/csv'
  if (isXlsxFile(file)) return XLSX_MEDIA_TYPE
  return file.type.toLowerCase()
}

/** Why this file can't be attached, or null if it's fine. */
export function attachmentRejection(
  file: File,
  usedBytes = 0,
  allowCsv = true
): string | null {
  const csvFile = isCsvFile(file)
  const xlsxFile = isXlsxFile(file)
  const lowerName = file.name.toLowerCase()
  if (lowerName.endsWith('.xls') || lowerName.endsWith('.xlsm')) {
    return 'Excel uploads must use the .xlsx format.'
  }
  if ((csvFile || xlsxFile) && !allowCsv) {
    return 'CSV and Excel files can only be attached to David.'
  }
  const mediaType = normalizedMediaType(file)
  if (
    !ACCEPTED_MEDIA_TYPES.includes(mediaType) ||
    (mediaType === 'text/csv' && !csvFile) ||
    (mediaType === XLSX_MEDIA_TYPE && !xlsxFile)
  ) {
    return `${file.name || 'That file'} isn't a PNG, JPEG, GIF, WebP, PDF, CSV or .xlsx file.`
  }
  const cap =
    mediaType === 'application/pdf'
      ? MAX_PDF_BYTES
      : isStorageBackedAttachment({ mediaType })
        ? MAX_CSV_BYTES
        : MAX_IMAGE_BYTES
  if (file.size > cap) {
    return `${file.name || 'That file'} is larger than ${cap / (1024 * 1024)} MB.`
  }
  if (
    !isStorageBackedAttachment({ mediaType }) &&
    usedBytes + file.size > MAX_TOTAL_BYTES
  ) {
    return `Attachments can't total more than ${MAX_TOTAL_BYTES / (1024 * 1024)} MB.`
  }
  return null
}

export function toPendingAttachment(file: File): PendingAttachment {
  return {
    id: crypto.randomUUID(),
    file,
    // A pasted screenshot arrives as "image.png"; keep something meaningful.
    filename: file.name || `pasted-${file.type.split('/')[1] || 'file'}`,
    mediaType: normalizedMediaType(file),
    url: URL.createObjectURL(file),
  }
}

/** The shape `MessageAttachment` renders, from either a pending or a sent file. */
export function toFileUIPart(attachment: {
  filename: string
  mediaType: string
  url?: string
}): FileUIPart {
  return {
    type: 'file',
    url: attachment.url ?? '',
    mediaType: attachment.mediaType,
    filename: attachment.filename,
  }
}

export function chatAttachmentRefs(
  attachments: PendingAttachment[]
): ChatAttachmentRef[] {
  return attachments.flatMap((attachment) =>
    !isStorageBackedAttachment(attachment) && attachment.fileId
      ? [{ fileId: attachment.fileId, filename: attachment.filename }]
      : []
  )
}

export function csvAttachmentRefs(
  attachments: PendingAttachment[]
): CsvAttachmentRef[] {
  return attachments.flatMap((attachment) =>
    isStorageBackedAttachment(attachment) && attachment.fileId
      ? [
          {
            fileId: attachment.fileId,
            filename: attachment.filename,
            sizeBytes: attachment.file.size,
          },
        ]
      : []
  )
}
